import React, { useMemo, useRef, useState } from 'react';
import { Select, Tag, Tooltip, message } from 'antd';
import { TagsOutlined } from '@ant-design/icons';

/**
 * The product labels on one row, editable in place.
 *
 * Tagging is something people do to a handful of rows in a row while scanning a
 * list, so it stays in the table: click the cell, pick or drop products, click
 * away and it saves. Opening a form per row for what is a two-second decision
 * is the thing this avoids.
 *
 * `value` is the row's `products_detail` (objects with id/name) and `options`
 * the full product list from subscription billing. `onSave` takes the new list
 * of product ids and must resolve only once the row it came from carries what
 * the server saved — the cell hands display back to `value` at that point.
 */
const ProductLabelsCell = ({ value, options = [], onSave, disabled = false }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState([]);

  // Set while a save is in flight so the tags shown are the ones just picked,
  // not the stale row the table is still holding.
  const [optimistic, setOptimistic] = useState(null);

  // Guards against onBlur firing a second save after the dropdown closes.
  const savingRef = useRef(false);

  const selected = useMemo(
    () => optimistic ?? (Array.isArray(value) ? value : []),
    [optimistic, value]
  );

  const selectOptions = useMemo(
    () => options.map((product) => ({ value: product.id, label: product.name })),
    [options]
  );

  const startEditing = () => {
    if (disabled) return;
    setDraft(selected.map((product) => product.id));
    setEditing(true);
  };

  const commit = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setEditing(false);

    const before = selected.map((product) => product.id);
    const unchanged =
      before.length === draft.length && before.every((id) => draft.includes(id));
    if (unchanged) {
      savingRef.current = false;
      return;
    }

    // Show the picked labels straight away, so the cell does not blink back
    // to the old ones while the save is in flight.
    setOptimistic(
      draft
        .map((id) => options.find((product) => product.id === id))
        .filter(Boolean)
    );
    setSaving(true);
    try {
      await onSave(draft);
      message.success('Products updated');
    } catch (e) {
      message.error('Could not update products');
    }
    // Either way the row is the truth again: saved, or never changed.
    setOptimistic(null);
    setSaving(false);
    savingRef.current = false;
  };

  if (editing) {
    return (
      <Select
        mode="multiple"
        autoFocus
        defaultOpen
        size="small"
        style={{ width: '100%', minWidth: 220 }}
        placeholder="Select products"
        value={draft}
        onChange={setDraft}
        onBlur={commit}
        options={selectOptions}
        optionFilterProp="label"
        maxTagCount="responsive"
      />
    );
  }

  return (
    <Tooltip title={disabled ? undefined : 'Click to edit products'}>
      <div
        onClick={startEditing}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          minHeight: 22,
          minWidth: 120,
          opacity: saving ? 0.5 : 1,
        }}
      >
        {selected.length ? (
          selected.map((product) => (
            <Tag key={product.id} color="blue" style={{ marginInlineEnd: 4 }}>
              {product.name}
            </Tag>
          ))
        ) : (
          <span style={{ color: '#bfbfbf' }}>
            <TagsOutlined /> Add
          </span>
        )}
      </div>
    </Tooltip>
  );
};

export default ProductLabelsCell;
